import {
	type CampaignKey,
	type DateKey,
	getOdataArray,
	OrderKey,
	Param,
	type SortValuesMode,
	URLSearchParser
} from 'shared/search-adapters'

import type { Page } from './types'

export type Mode = 'Add' | 'DraftCampaign' | 'Executions' | 'Import'

const IdParser: Record<Mode, (id: string) => string> = {
	Add: id => `not (${IdParser.DraftCampaign(id)})`,
	DraftCampaign: id => `campaigns/any(c:c eq '${id}')`,
	Executions: id => `campaign_id eq '${id}'`,
	Import: id => `tags/any(c:c eq '${id}')`
}

type QueryParams = Partial<Page>

const orderKeyToOData: Record<OrderKey, string> = {
	[OrderKey.Ascending]: 'asc',
	[OrderKey.Descending]: 'desc'
}

const modeToSortMode: Record<Mode, SortValuesMode> = {
	Add: 'General',
	DraftCampaign: 'CampaignContacts',
	Executions: 'Executions',
	Import: 'General'
}

function mapDates(params: URLSearchParams, key: DateKey, name: string) {
	const dates = URLSearchParser.Array(params, key)
	if (dates?.length !== 2) {
		return ''
	}

	const [start, end] = dates
	return `(${name} gt ${start} and ${name} lt ${end})`
}

const mapCampaigns = (params: URLSearchParams, key: CampaignKey) =>
	URLSearchParser.Campaigns(params, key)
		.map(({ id }) => `campaigns/any(c:c eq '${id}')`)
		.join(' or ')

export class FilterParser {
	get query() {
		return this.#queryBody
	}

	get result() {
		this.#saveFilters()

		return {
			isApplied: this.#isFilterApplied,
			params: this.#queryBody
		}
	}
	#filters: string[] = []

	#isFilterApplied = false

	#queryBody: QueryParams = {}

	constructor(
		private params: URLSearchParams,
		private mode: Mode = 'Import'
	) {
		this.#setUpInitial()
		this.#parseSearch()
		this.#parseOrder()
		this.#parseFilters()
	}

	saveId(value: string) {
		this.#filters.push(IdParser[this.mode](value))
	}

	#parseContactFilters() {
		let nextFilter = URLSearchParser.ArrayToFilter(
			this.params,
			Param.Distance,
			'distance'
		)
		nextFilter && this.#filters.push(nextFilter)

		nextFilter = URLSearchParser.ArrayToFilter(
			this.params,
			Param.Locations,
			'location',
			true
		)
		nextFilter && this.#filters.push(nextFilter)

		nextFilter = mapDates(this.params, Param.LastContacted, 'contacted')
		nextFilter && this.#filters.push(nextFilter)

		nextFilter = mapDates(this.params, Param.NotContacted, 'contacted')
		nextFilter && this.#filters.push(`not ${nextFilter}`)

		nextFilter = mapDates(this.params, Param.Created, 'created')
		nextFilter && this.#filters.push(nextFilter)

		nextFilter = mapDates(this.params, Param.NotCreated, 'created')
		nextFilter && this.#filters.push(`not ${nextFilter}`)

		nextFilter = mapCampaigns(this.params, Param.Campaigns)
		nextFilter && this.#filters.push(`(${nextFilter})`)

		const values = URLSearchParser.QualificationValues(
			this.params,
			this.mode === 'Add'
		)
		nextFilter = values.length
			? getOdataArray(values, 'qualification', true)
			: ''
		nextFilter && this.#filters.push(nextFilter)

		const notQualificationValues = URLSearchParser.Array(
			this.params,
			Param.NotLinkedInQualification
		)
		nextFilter = notQualificationValues.length
			? getOdataArray(notQualificationValues, 'qualification', true)
			: ''
		nextFilter && this.#filters.push(`not (${nextFilter})`)

		nextFilter = mapCampaigns(this.params, Param.NotCampaigns)
		nextFilter && this.#filters.push(`not (${nextFilter})`)

		if (URLSearchParser.Boolean(this.params, Param.Premium)) {
			this.#filters.push('is_premium eq true')
		}

		const avatarValue = this.params.get(Param.Avatar)
		if (avatarValue === '1') {
			this.#filters.push('picture_id ne null')
		} else if (avatarValue === '2') {
			this.#filters.push('picture_id eq null')
		}

		const notAvatarValue = this.params.get(Param.NotAvatar)
		if (notAvatarValue === '1') {
			this.#filters.push('not (picture_id ne null)')
		} else if (notAvatarValue === '2') {
			this.#filters.push('not (picture_id eq null)')
		}

		this.#reCheckApplied()
	}

	#parseExecutionsFilters() {
		let nextFilter = URLSearchParser.ArrayToFilter(
			this.params,
			Param.ExecutionDistance,
			'linkedin_contact/distance'
		)

		nextFilter && this.#filters.push(nextFilter)
		nextFilter = URLSearchParser.ArrayToFilter(
			this.params,
			Param.ExecutionLocations,
			'linkedin_contact/location',
			true
		)
		nextFilter && this.#filters.push(nextFilter)

		nextFilter = URLSearchParser.ArrayToFilter(
			this.params,
			Param.ExecutionStatus,
			'state',
			true
		)
		nextFilter && this.#filters.push(nextFilter)

		const values = URLSearchParser.QualificationValues(this.params)
		nextFilter = values.length
			? getOdataArray(values, 'linkedin_contact/qualification', true)
			: ''
		nextFilter && this.#filters.push(nextFilter)

		nextFilter = URLSearchParser.GotAnser(this.params)
		nextFilter && this.#filters.push(nextFilter)

		if (URLSearchParser.Boolean(this.params, Param.ExecutionPremium)) {
			this.#filters.push('linkedin_contact/is_premium eq true')
		}

		this.#reCheckApplied()
	}

	#parseFilters() {
		switch (this.mode) {
			case 'Add':
			case 'Import':
				return this.#parseContactFilters()
			case 'DraftCampaign': {
				return
			}

			case 'Executions': {
				return this.#parseExecutionsFilters()
			}
		}
	}

	#parseOrder() {
		const { orderBy, sortBy } = URLSearchParser.Sort(
			this.params,
			this.mode === 'Add' ? Param.OrderModal : Param.Order,
			modeToSortMode[this.mode]
		)

		const odataOrder = orderKeyToOData[orderBy]

		let result = `${sortBy} ${odataOrder}`
		if (sortBy === 'created') {
			result += `, id ${odataOrder}`
		}

		this.#queryBody.$orderby = result
	}

	#parseSearch() {
		const key = this.mode === 'Add' ? Param.ModalSearchQuery : Param.SearchQuery

		const query = this.params.get(key)

		if (query) {
			this.#queryBody.$searchQuery = query
			this.#reCheckApplied()
		}
	}

	#reCheckApplied() {
		this.#isFilterApplied ||= Boolean(
			this.#queryBody.$searchQuery || this.#filters.length
		)
	}

	#saveFilters() {
		if (this.#filters.length) {
			this.#queryBody.$filter = this.#filters.join(' and ')
		}
	}

	#setUpInitial() {
		const skipKey = this.mode === 'Add' ? Param.ModalSkip : Param.Skip
		const skip = URLSearchParser.Skip(this.params, skipKey)
		this.#isFilterApplied ||= Boolean(skip)

		this.#queryBody = {
			$skip: skip,
			$take: URLSearchParser.PageSize(this.params)
		}
	}
}
